/**
 * Tema visual del portal: `var` (el sistema de diseño VAR Technology, el
 * predeterminado) o `clasico` (el diseño anterior, intacto).
 *
 * El tema es un atributo `data-theme` en `<html>`: `styles.css` define el
 * clásico en `:root` y `theme-var.css` redefine los tokens bajo
 * `:root[data-theme="var"]`. Ningún componente sabe qué tema está activo.
 *
 * Se elige en Ajustes (persistido con el resto de los settings) o por URL con
 * `?tema=clasico` / `?tema=var`, que es la única puerta del portal público
 * (no tiene Ajustes). El parámetro gana sobre lo guardado y se recuerda.
 *
 * Este archivo no importa React y tolera no tener `document` (tests en Node).
 */

export type Theme = 'var' | 'clasico';

export const THEMES: readonly Theme[] = ['var', 'clasico'];

export const DEFAULT_THEME: Theme = 'var';

/** Nombre del parámetro de URL que fuerza el tema. */
export const THEME_PARAM = 'tema';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/**
 * Tema pedido por URL, o `null`. Mira el query string real y también el que
 * cuelga del hash (`#/matches?tema=clasico`), porque el portal técnico rutea
 * por hash y es natural pegarlo ahí.
 */
export function themeFromUrl(search: string, hash = ''): Theme | null {
  const candidates = [search, hash.includes('?') ? hash.slice(hash.indexOf('?')) : ''];
  for (const query of candidates) {
    if (query === '') continue;
    const value = new URLSearchParams(query).get(THEME_PARAM);
    if (isTheme(value)) return value;
  }
  return null;
}

/** Aplica el tema al documento (no-op sin DOM). */
export function applyTheme(theme: Theme): void {
  const g = globalThis as { document?: { documentElement?: { dataset?: Record<string, string> } } };
  const root = g.document?.documentElement;
  if (root?.dataset === undefined) return;
  if (root.dataset['theme'] !== theme) root.dataset['theme'] = theme;
}
